import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAppContext } from 'src/contexts/AppContext';
import {
  Content,
  FormatContent,
  getContent,
  useContent,
  useContentContext,
} from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { PosCurrencyField } from 'src/core/POS/PosCurrencyField';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { getTextFieldState, PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Radio, RadioGroup, Stack, TooltipPopover } from 'src/core/ui';
import { InfoOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { FLIGHT_DURATION_TO_CID } from 'src/utils/constants/contentIdMaps';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { posChangedField, posFieldFromValues } from 'src/utils/posFieldUtils';
import {
  Event,
  FlightDuration,
  ListingMetrics,
  SponsoredListingsCampaign,
  SponsoredListingsCampaignInput,
} from 'src/WebApiController';

import { getProspectiveBudget } from '../sponsoredListings.util';
import * as styles from './CampaignModal.css';

export const CampaignModalBody = ({
  campaign,
  event,
  disabled,
  eventNameDisplay,
  metrics,
}: {
  campaign?: SponsoredListingsCampaign;
  event: Event;
  disabled: boolean;
  eventNameDisplay: string;
  metrics?: ListingMetrics;
}) => {
  const { isDarkMode } = useSiteTheme();
  const { loginContext } = useAppContext();
  const { getUiCurrency } = useLocalizationContext();
  const [hasManualNameChanged, setHasManualNameChanged] = useState(false);
  const [hasManualBudgetChanged, setHasManualBudgetChanged] = useState(false);
  const uiCurrency = useMemo(
    () => getUiCurrency(loginContext?.user?.activeAccount?.currencyCode),
    [getUiCurrency, loginContext]
  );

  const contentContext = useContentContext();

  const {
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext<SponsoredListingsCampaignInput>();

  const campaignNamePlaceholder = useContent(ContentId.EnterAdCampaignName);

  const campaignId = watch('campaignId');
  const campaignName = watch('campaignName');
  const flightDuration = watch('flightDuration');
  const recurringFlightBudget = watch('recurringFlightBudget');
  const targetTicketClassIds = watch('targetTicketClassIds');
  const editBudgetForActiveFlightOnly = watch('editBudgetForActiveFlightOnly');

  const flightDurationText = useMemo(() => {
    switch (flightDuration?.value) {
      case FlightDuration.Daily:
        return getContent(ContentId.Today, contentContext).toLocaleLowerCase();
      case FlightDuration.Weekly:
        return getContent(
          ContentId.ThisWeek,
          contentContext
        ).toLocaleLowerCase();
      case FlightDuration.Monthly:
        return getContent(
          ContentId.ThisMonth,
          contentContext
        ).toLocaleLowerCase();
      default:
        return getContent(
          ContentId.TheCurrentFlight,
          contentContext
        ).toLocaleLowerCase();
    }
  }, [contentContext, flightDuration?.value]);

  return (
    <Stack
      direction="column"
      gap="l"
      width="full"
      className={styles.boostBodyContainer}
    >
      <Stack gap="l" width="full">
        <Stack direction="column" gap="l">
          <Stack gap="m" alignItems="center" className={styles.itemLabel}>
            <span>1.</span>
            <Content id={ContentId.CampaignName} />
          </Stack>
          <Stack gap="m" alignItems="center" className={styles.itemLabel}>
            <span>2.</span>
            <PosEnumSelect
              className={styles.itemLabel}
              fontWeight={400}
              disabled={disabled}
              variant="textPlain"
              value={flightDuration?.value ?? FlightDuration.Daily}
              valueOptionsContent={FLIGHT_DURATION_TO_CID}
              sortMode="none"
              onChange={(option: FlightDuration | null) => {
                if (option) {
                  setValue(
                    'flightDuration',
                    posFieldFromValues(option, campaign?.flightDuration)
                  );
                  if (campaignId <= 0) {
                    // If new campaign, apply auto-generation of names and budget logic
                    if (!hasManualNameChanged) {
                      setValue(
                        'campaignName',
                        posFieldFromValues(
                          `${eventNameDisplay} - ${option}`,
                          campaign?.campaignName
                        )
                      );
                    }
                    if (!hasManualBudgetChanged) {
                      const newBudget = getProspectiveBudget(
                        metrics?.unsoldCst,
                        option,
                        event.dates.start
                      );
                      if (newBudget != null) {
                        setValue(
                          'recurringFlightBudget',
                          posFieldFromValues(
                            newBudget,
                            campaign?.recurringFlightBudget
                          )
                        );
                      }
                    }
                  } else {
                    // Changing flight duration uncheck edit budget for active flight only
                    setValue(
                      'editBudgetForActiveFlightOnly',
                      posChangedField(false)
                    );
                  }
                }
              }}
            />
            <TooltipPopover
              contentVariant={isDarkMode ? 'light' : 'dark'}
              triggerContent={
                <InfoOutlineIcon
                  withHoverEffect
                  fill={vars.color.iconPrimary}
                  size={vars.iconSize.s}
                />
              }
            >
              <Content id={ContentId.FlightBudgetDurationHelpInfo} />
            </TooltipPopover>
          </Stack>
        </Stack>
        <Stack direction="column" gap="l" width="full">
          <PosFormField errors={errors.campaignName?.message}>
            <PosTextField
              disabled={disabled}
              errorState={getTextFieldState(errors.campaignName)}
              value={campaignName?.value ?? ''}
              placeholder={campaignNamePlaceholder}
              type="text"
              inputMode="text"
              onChange={(e) => {
                if (e.target.value) {
                  setHasManualNameChanged(true);
                  setValue(
                    'campaignName',
                    posFieldFromValues(e.target.value, campaign?.campaignName)
                  );
                  clearErrors('campaignName');
                }
              }}
            />
          </PosFormField>
          <PosFormField errors={errors.recurringFlightBudget?.message}>
            <PosCurrencyField
              disabled={disabled}
              errorState={getTextFieldState(errors.recurringFlightBudget)}
              value={recurringFlightBudget?.value ?? ''}
              onChange={(e) => {
                const newNumber = Number(e.target.value);
                if (newNumber >= 0) {
                  setHasManualBudgetChanged(true);
                  setValue(
                    'recurringFlightBudget',
                    posFieldFromValues(
                      newNumber,
                      campaign?.recurringFlightBudget
                    )
                  );
                  clearErrors('recurringFlightBudget');
                }
              }}
              uiCurrency={uiCurrency}
            />
          </PosFormField>
        </Stack>
      </Stack>
      {campaign?.activeFlight && (
        <Checkbox
          checked={editBudgetForActiveFlightOnly?.value ?? false}
          disabled={disabled}
          labelPosition="right"
          label={
            <FormatContent
              id={FormatContentId.EditBudgetForActiveFlightOnly}
              params={[
                flightDurationText,
                `${format(new Date(campaign.activeFlight.dateRange.end!), 'MMM dd, yyyy')}`,
              ]}
            />
          }
          onChange={(checked) => {
            setValue(
              'editBudgetForActiveFlightOnly',
              posChangedField(checked.target.checked)
            );
            if (!checked.target.checked) {
              // If unchecking, reset budget to recurring flight budget
              setValue(
                'recurringFlightBudget',
                posFieldFromValues(
                  recurringFlightBudget?.value,
                  campaign?.recurringFlightBudget
                )
              );
            } else {
              // If checking, set budget to active flight budget
              setValue(
                'recurringFlightBudget',
                posFieldFromValues(
                  recurringFlightBudget?.value,
                  campaign?.activeFlight?.flightBudget
                )
              );
              // Also set flight duration back to existing
              setValue(
                'flightDuration',
                posFieldFromValues(campaign.flightDuration)
              );
            }
          }}
        />
      )}
      <Stack direction="column" gap="m" width="full">
        <Stack gap="m" alignItems="center" className={styles.itemLabel}>
          <span>3.</span>
          <Content id={ContentId.Bidding} />
        </Stack>
        <RadioGroup value="automatic" disabled={disabled}>
          <Radio
            disabled={disabled}
            value="automatic"
            variant="outline"
            label={<Content id={ContentId.Automatic} />}
          />
        </RadioGroup>
      </Stack>
      <Stack direction="column" gap="m" width="full">
        <Stack gap="m" alignItems="center" className={styles.itemLabel}>
          <span>4.</span>
          <Content id={ContentId.Target} />
          <TooltipPopover
            contentVariant={isDarkMode ? 'light' : 'dark'}
            triggerContent={
              <InfoOutlineIcon
                withHoverEffect
                fill={vars.color.iconPrimary}
                size={vars.iconSize.s}
              />
            }
          >
            <Content id={ContentId.CampaignTargetHelpInfo} />
          </TooltipPopover>
        </Stack>
        <RadioGroup
          disabled={disabled}
          value={targetTicketClassIds?.value?.length ? 'zone' : 'default'}
          onValueChange={(value) => {
            if (value === 'default') {
              setValue(
                'targetTicketClassIds',
                posFieldFromValues(null, campaign?.targetTicketClassIds)
              );
            } else if (value === 'zone') {
              alert('TODO - display map selector');
            }
          }}
        >
          <Radio
            disabled={disabled}
            value="default"
            variant="outline"
            label={<Content id={ContentId.AllZones} />}
            rightContent={
              <span className={styles.itemMetadata}>
                <Content id={ContentId.Recommended} />
              </span>
            }
          />
          <Radio
            disabled={disabled}
            value="zone"
            variant="outline"
            label={<Content id={ContentId.SpecificZones} />}
          />
        </RadioGroup>
      </Stack>
    </Stack>
  );
};
